/**
 * Zod Schema 定义
 */

import { z } from 'zod';

// 组件属性 Schema
export const componentPropertySchema = z.object({
  name: z.string(),
  value: z.union([z.string(), z.boolean()]),
  type: z.enum(['VARIANT', 'BOOLEAN', 'TEXT', 'INSTANCE_SWAP']),
});

// 基础 Figma 节点 Schema（不包含 children）
const baseFigmaNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  layout: z.record(z.any()).optional(),
  fills: z.record(z.any()).optional(),
  strokes: z.record(z.any()).optional(),
  effects: z.record(z.any()).optional(),
  componentId: z.string().optional(),
  componentProperties: z.array(componentPropertySchema).optional(),
  textStyle: z.record(z.any()).optional(),
  borderRadius: z.union([z.number(), z.string()]).optional(),
  opacity: z.number().optional(),
});

// 递归 Figma 节点 Schema
export const figmaNodeSchema: z.ZodType<{
  id: string;
  name: string;
  type: string;
  children?: Array<{
    id: string;
    name: string;
    type: string;
    children?: any[];
    layout?: Record<string, any>;
    fills?: Record<string, any>;
    strokes?: Record<string, any>;
    effects?: Record<string, any>;
    componentId?: string;
    componentProperties?: Array<{
      name: string;
      value: string | boolean;
      type: 'VARIANT' | 'BOOLEAN' | 'TEXT' | 'INSTANCE_SWAP';
    }>;
    textStyle?: Record<string, any>;
    borderRadius?: number | string;
    opacity?: number;
  }>;
  layout?: Record<string, any>;
  fills?: Record<string, any>;
  strokes?: Record<string, any>;
  effects?: Record<string, any>;
  componentId?: string;
  componentProperties?: Array<{
    name: string;
    value: string | boolean;
    type: 'VARIANT' | 'BOOLEAN' | 'TEXT' | 'INSTANCE_SWAP';
  }>;
  textStyle?: Record<string, any>;
  borderRadius?: number | string;
  opacity?: number;
}> = baseFigmaNodeSchema.extend({
  children: z.array(z.lazy(() => figmaNodeSchema)).optional(),
});

// Figma 组件 Schema
export const figmaComponentSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  componentSetId: z.string().optional(),
});

// Figma 组件集 Schema
export const figmaComponentSetSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
});

// Figma 文件 Schema
export const figmaFileSchema = z.object({
  name: z.string(),
  metadata: z.record(z.any()),
  components: z.record(figmaComponentSchema),
  componentSets: z.record(figmaComponentSetSchema),
  nodes: z.array(figmaNodeSchema),
});

// 设计令牌 Schema
export const designTokenSchema = z.object({
  name: z.string(),
  value: z.union([z.string(), z.number()]),
  type: z.enum(['color', 'spacing', 'typography', 'border-radius', 'shadow']),
  category: z.string(),
  description: z.string().optional(),
});

// 基础组件树 Schema（不包含 children）
const baseComponentTreeSchema = z.object({
  node: figmaNodeSchema,
  depth: z.number(),
  path: z.array(z.string()),
});

// 递归组件树 Schema
export const componentTreeSchema: z.ZodType<{
  node: any;
  children: Array<{
    node: any;
    children: any[];
    depth: number;
    path: string[];
  }>;
  depth: number;
  path: string[];
}> = baseComponentTreeSchema.extend({
  children: z.array(z.lazy(() => componentTreeSchema)),
});

// 规则修复建议 Schema
export const ruleFixSchema = z.object({
  type: z.enum(['replace', 'add', 'remove', 'modify']),
  target: z.string(),
  value: z.any(),
  description: z.string(),
});

// 规则结果 Schema
export const ruleResultSchema = z.object({
  valid: z.boolean(),
  message: z.string(),
  severity: z.enum(['error', 'warning', 'info']),
  suggestions: z.array(z.string()).optional(),
  fixes: z.array(ruleFixSchema).optional(),
});

// 规则上下文 Schema
export const ruleContextSchema = z.object({
  traceId: z.string(),
  node: figmaNodeSchema,
  parent: figmaNodeSchema.optional(),
  siblings: z.array(figmaNodeSchema),
  componentTree: componentTreeSchema,
  designTokens: z.array(designTokenSchema),
  figmaFile: figmaFileSchema,
});

// 验证结果 Schema
export const validationResultSchema = z.object({
  traceId: z.string(),
  nodeId: z.string(),
  nodeName: z.string(),
  rules: z.array(z.object({
    ruleName: z.string(),
    result: ruleResultSchema,
  })),
  timestamp: z.date(),
});

// 规则配置 Schema
export const ruleConfigSchema = z.object({
  enabled: z.boolean(),
  severity: z.enum(['error', 'warning', 'info']).optional(),
  options: z.record(z.any()).optional(),
});

// 规则引擎配置 Schema
export const ruleEngineConfigSchema = z.object({
  rules: z.record(ruleConfigSchema),
  globalOptions: z.object({
    traceId: z.string().optional(),
    logLevel: z.enum(['debug', 'info', 'warn', 'error']).optional(),
    parallel: z.boolean().optional(),
    maxConcurrency: z.number().optional(),
  }),

});

// 日志条目 Schema
export const logEntrySchema = z.object({
  traceId: z.string(),
  level: z.enum(['debug', 'info', 'warn', 'error']),
  message: z.string(),
  timestamp: z.date(),
  context: z.record(z.any()).optional(),
});
// ===== 扩展的 Figma 处理 Schema =====

// 简化布局 Schema
export const simplifiedLayoutSchema = z.object({
  mode: z.enum(["none", "row", "column"]),
  justifyContent: z.enum(["flex-start", "flex-end", "center", "space-between", "baseline", "stretch"]).optional(),
  alignItems: z.enum(["flex-start", "flex-end", "center", "space-between", "baseline", "stretch"]).optional(),
  alignSelf: z.enum(["flex-start", "flex-end", "center", "stretch"]).optional(),
  wrap: z.boolean().optional(),
  gap: z.string().optional(),
  locationRelativeToParent: z.object({
    x: z.number(),
    y: z.number()
  }).optional(),
  dimensions: z.object({
    width: z.number().optional(),
    height: z.number().optional(),
    aspectRatio: z.number().optional()
  }).optional(),
  padding: z.string().optional(),
  sizing: z.object({
    horizontal: z.enum(["fixed", "fill", "hug"]).optional(),
    vertical: z.enum(["fixed", "fill", "hug"]).optional()
  }).optional(),
  overflowScroll: z.array(z.enum(["x", "y"])).optional(),
  position: z.literal("absolute").optional()
});

// 简化文本样式 Schema
export const simplifiedTextStyleSchema = z.object({
  fontFamily: z.string().optional(),
  fontWeight: z.number().optional(),
  fontSize: z.number().optional(),
  lineHeight: z.number().optional(),
  letterSpacing: z.number().optional(),
  textAlign: z.enum(["left", "center", "right", "justify"]).optional(),
  textDecoration: z.string().optional(),
  textTransform: z.string().optional(),
  color: z.string().optional()
});

// 简化填充样式 Schema
export const simplifiedFillSchema = z.object({
  type: z.enum(["solid", "gradient", "image"]),
  color: z.string().optional(),
  opacity: z.number().optional(),
  gradient: z.object({
    type: z.string(),
    stops: z.array(z.object({
      color: z.string(),
      position: z.number()
    }))
  }).optional(),
  imageRef: z.string().optional()
});

// 简化描边样式 Schema
export const simplifiedStrokeSchema = z.object({
  colors: z.array(z.string()),
  width: z.number(),
  style: z.enum(["solid", "dashed", "dotted"]).optional(),
  position: z.enum(["inside", "outside", "center"]).optional()
});

// 简化效果样式 Schema
export const simplifiedEffectSchema = z.object({
  type: z.enum(["drop-shadow", "inner-shadow", "blur", "background-blur"]),
  color: z.string().optional(),
  offset: z.object({
    x: z.number(),
    y: z.number()
  }).optional(),
  radius: z.number().optional(),
  spread: z.number().optional()
});

// 预处理统计 Schema
export const preprocessingStatsSchema = z.object({
  totalOriginal: z.number(),
  filtered: z.number(),
  retained: z.number(),
  filteringRate: z.number()
});

// 过滤元素 Schema
export const filteredElementSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  reason: z.enum(["transparent", "hidden", "empty", "abnormal_size", "invalid_type", "duplicate"]),
  details: z.string()
});

// 预处理结果 Schema
export const preprocessingResultSchema = z.object({
  validNodes: z.array(figmaNodeSchema),
  filteredElements: z.array(filteredElementSchema),
  stats: preprocessingStatsSchema
});

// 布局问题 Schema
export const layoutIssueSchema = z.object({
  type: z.enum(["overlap", "misalignment", "inconsistent_spacing", "poor_hierarchy"]),
  severity: z.enum(["low", "medium", "high"]),
  description: z.string(),
  affectedNodes: z.array(z.string())
});

// 布局分析结果 Schema
export const layoutAnalysisResultSchema = z.object({
  layoutType: z.enum(["flex", "grid", "absolute", "mixed"]),
  complexity: z.enum(["simple", "moderate", "complex"]),
  issues: z.array(layoutIssueSchema),
  recommendations: z.array(z.string())
});

// 样式优化结果 Schema
export const styleOptimizationResultSchema = z.object({
  duplicateStyles: z.array(z.object({
    styleType: z.string(),
    duplicateCount: z.number(),
    nodes: z.array(z.string())
  })),
  unusedStyles: z.array(z.string()),
  optimizationSuggestions: z.array(z.string())
});

// 全局变量存储 Schema
export const globalVarsSchema = z.object({
  styles: z.record(z.union([
    simplifiedLayoutSchema,
    simplifiedTextStyleSchema,
    z.array(simplifiedFillSchema),
    simplifiedStrokeSchema,
    z.array(simplifiedEffectSchema)
  ]))
});

// 提取上下文 Schema
export const extractionContextSchema = z.object({
  globalVars: globalVarsSchema,
  parent: figmaNodeSchema.optional(),
  depth: z.number(),
  path: z.array(z.string())
});

// 简化节点 Schema (递归类型)
export const simplifiedNodeSchema: z.ZodType<any> = z.lazy(() => z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  layout: simplifiedLayoutSchema.optional(),
  textStyle: simplifiedTextStyleSchema.optional(),
  fills: z.array(simplifiedFillSchema).optional(),
  strokes: z.array(simplifiedStrokeSchema).optional(),
  effects: z.array(simplifiedEffectSchema).optional(),
  children: z.array(simplifiedNodeSchema).optional(),
  text: z.string().optional(),
  visible: z.boolean().optional(),
  opacity: z.number().optional()
}));